import { useMemo, useState } from "react";
import {
  Button, Card, Checkbox, Col, Collapse, Divider, Dropdown, InputNumber, Popconfirm,
  Radio, Row, Select, Space, Table, Tabs, Typography, Upload, message,
} from "antd";
import {
  CheckCircleOutlined, DownloadOutlined, FileExcelOutlined,
  FilePdfOutlined, PlusOutlined, UploadOutlined, EyeOutlined,
  EyeInvisibleOutlined, MailOutlined, IdcardOutlined, UserOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import Search from "antd/lib/input/Search";
import { useHuulga } from "./useHuulga";
import { HUULGA_QUARTERS, HuulgaQuarter, HuulgaQuarterFileTypeKey } from "./types";

const { Panel } = Collapse;
const { TabPane } = Tabs;

type QuarterFileTypeKey = HuulgaQuarterFileTypeKey;

const QUARTER_FILE_TYPES: { key: QuarterFileTypeKey; label: string }[] = [
  { key: "niitHudaldanAvaltText", label: "Нийт худалдан авалт" },
  { key: "niitTsenegleltText", label: "Нийт цэнэглэлт" },
  { key: "buitsaaltBondText", label: "Буцаалт (Бонд)" },
  { key: "buitsaaltStockText", label: "Буцаалт (Хувьцаа)" },
];

export const HuulgaWidget = () => {
  const {
    loading, dataLoading, showFileUpload, setShowFileUpload,
    result, quarterResult, error, searchQuarter, downloadExcel, downloadPDF,
    availableYears, availableQuarterYears, availableYearQuarterKeys,
    selectedUploadYear, setSelectedUploadYear, selectedUploadQuarter,
    setSelectedUploadQuarter, addYearQuarter, handleQuarterFileUpload,
    deleteYearQuarterFiles, isQuarterFileUploaded, filterMode, setFilterMode,
    selectedQuarterKeys, setSelectedQuarterKeys,
  } = useHuulga();

  const [newYear, setNewYear] = useState<number | null>(null);
  const [newQuarter, setNewQuarter] = useState<HuulgaQuarter>("Q1");

  const uploadYearOptions = useMemo(
    () =>
      [...new Set([selectedUploadYear, ...availableYears, ...availableQuarterYears])].sort(
        (a, b) => a - b
      ),
    [availableQuarterYears, availableYears, selectedUploadYear]
  );

  const yearColumn = {
    title: "Он",
    dataIndex: "year",
    key: "year",
    width: 70,
    render: (year: number) => <Typography.Text strong>{year}</Typography.Text>,
  };

  const orlogoZarlagaColumns = [
    yearColumn,
    { title: "Огноо", dataIndex: "date", key: "date", width: 180 },
    { title: "Орлого", dataIndex: "income", key: "income", width: 150 },
    { title: "Зарлага", dataIndex: "expense", key: "expense", width: 150 },
    { title: "Тайлбар", dataIndex: "description", key: "description" },
    { title: "Цэнэглэсэн огноо / Данс", dataIndex: "depositDate", key: "depositDate", width: 200 },
  ];

  const hudaldanAvaltColumns = [
    yearColumn,
    { title: "Огноо", dataIndex: "date", key: "date", width: 180 },
    { title: "Мөнгөн дүн", dataIndex: "amount", key: "amount", width: 130 },
    { title: "Багц №", dataIndex: "batchNo", key: "batchNo", width: 80 },
    { title: "Bond", dataIndex: "bond", key: "bond", width: 120 },
    { title: "Estate", dataIndex: "estate", key: "estate", width: 100 },
    { title: "Худалдан авалтаас үлдсэн дүн", dataIndex: "lockAmount", key: "lockAmount", width: 150 },
    { title: "FX", dataIndex: "fx", key: "fx", width: 80 },
    { title: "DIA", dataIndex: "dia", key: "dia", width: 80 },
    { title: "VDE", dataIndex: "vde", key: "vde", width: 80 },
    { title: "VUG", dataIndex: "vug", key: "vug", width: 80 },
    { title: "VXF", dataIndex: "vxf", key: "vxf", width: 80 },
  ];

  const buitsaaltColumns = [
    yearColumn,
    { title: "Тооцоолсон огноо", dataIndex: "calcDate", key: "calcDate", width: 140 },
    { title: "Хөрөнгийн нэр", dataIndex: "assetName", key: "assetName", width: 140 },
    { title: "Үндсэн дүн", dataIndex: "principal", key: "principal", width: 120 },
    { title: "Бонд хүү", dataIndex: "bondInterest", key: "bondInterest", width: 100 },
    { title: "Хүү тооцох хоног", dataIndex: "days", key: "days", width: 120 },
    { title: "Тооцсон хүү", dataIndex: "calcInterest", key: "calcInterest", width: 120 },
    { title: "Нийт", dataIndex: "total", key: "total", width: 120 },
    { title: "AvtaMata шимтгэл", dataIndex: "avtamataFee", key: "avtamataFee", width: 140 },
    { title: "Арилжааны шимтгэл", dataIndex: "tradingFee", key: "tradingFee", width: 140 },
    { title: "ОАТ", dataIndex: "tax", key: "tax", width: 80 },
    { title: "Шилжүүлэх дүн", dataIndex: "transferAmount", key: "transferAmount", width: 130 },
    { title: "Данс", dataIndex: "account", key: "account", width: 130 },
  ];

  const quarterLabelColumn = {
    title: "Улирал",
    dataIndex: "quarter",
    key: "quarter",
    width: 80,
  };

  const quarterPurchaseColumns = useMemo(
    () => [
      yearColumn,
      quarterLabelColumn,
      { title: "Огноо", dataIndex: "date", key: "date", width: 140 },
      { title: "Огноо/Цаг", dataIndex: "dateTime", key: "dateTime", width: 180 },
      { title: "Мөнгөн дүн", dataIndex: "amount", key: "amount", width: 130 },
      { title: "Багц №", dataIndex: "batchNo", key: "batchNo", width: 90 },
      { title: "Бондын нэр", dataIndex: "bondName", key: "bondName", width: 160 },
      { title: "Bond", dataIndex: "bond", key: "bond", width: 120 },
      { title: "Битүүмжлэгдсэн дүн", dataIndex: "lockAmount", key: "lockAmount", width: 150 },
      ...(quarterResult?.holdingKeys || []).map((holdingKey) => ({
        title: holdingKey,
        dataIndex: ["holdings", holdingKey],
        key: holdingKey,
        width: 110,
      })),
    ],
    [quarterResult?.holdingKeys, yearColumn]
  );

  const quarterDepositColumns = [
    yearColumn,
    quarterLabelColumn,
    { title: "Огноо", dataIndex: "date", key: "date", width: 160 },
    { title: "Дүн", dataIndex: "amount", key: "amount", width: 140 },
    { title: "Зориулалт", dataIndex: "purpose", key: "purpose", width: 160 },
    { title: "Тайлбар", dataIndex: "description", key: "description", width: 220 },
    { title: "Цэнэглэсэн огноо / Данс", dataIndex: "depositDate", key: "depositDate", width: 200 },
  ];

  const quarterBondRefundColumns = [
    yearColumn,
    quarterLabelColumn,
    { title: "Тооцоолсон огноо", dataIndex: "calcDate", key: "calcDate", width: 140 },
    { title: "Хөрөнгийн нэр", dataIndex: "assetName", key: "assetName", width: 160 },
    { title: "Үндсэн дүн", dataIndex: "principal", key: "principal", width: 130 },
    { title: "Хүү %", dataIndex: "bondInterestRate", key: "bondInterestRate", width: 100 },
    { title: "Худалдан авсан огноо", dataIndex: "purchaseDate", key: "purchaseDate", width: 140 },
    { title: "Хүү тооцох хоног", dataIndex: "days", key: "days", width: 120 },
    { title: "Тооцсон хүү", dataIndex: "calcInterest", key: "calcInterest", width: 130 },
    { title: "30% хүү", dataIndex: "interestThirtyPercent", key: "interestThirtyPercent", width: 120 },
    { title: "Нийт", dataIndex: "total", key: "total", width: 120 },
    { title: "AvtaMata шимтгэл", dataIndex: "avtamataFee", key: "avtamataFee", width: 140 },
    { title: "Арилжааны шимтгэл", dataIndex: "tradingFee", key: "tradingFee", width: 140 },
    { title: "ОАТ", dataIndex: "tax", key: "tax", width: 100 },
    { title: "Шилжүүлэх дүн", dataIndex: "transferAmount", key: "transferAmount", width: 140 },
    { title: "Данс", dataIndex: "account", key: "account", width: 140 },
  ];

  const quarterStockRefundColumns = [
    yearColumn,
    quarterLabelColumn,
    { title: "Худалдан авсан огноо", dataIndex: "purchaseDate", key: "purchaseDate", width: 140 },
    { title: "Хөрөнгийн нэр", dataIndex: "stockName", key: "stockName", width: 160 },
    { title: "Төрөл", dataIndex: "assetType", key: "assetType", width: 110 },
    { title: "Тоо ширхэг", dataIndex: "quantity", key: "quantity", width: 110 },
    { title: "Ширхэгийн үнэ", dataIndex: "stockPrice", key: "stockPrice", width: 120 },
    { title: "USD rate", dataIndex: "usdRate", key: "usdRate", width: 100 },
    { title: "Нийт", dataIndex: "grossTotal", key: "grossTotal", width: 120 },
    { title: "Арилжааны шимтгэл", dataIndex: "tradingFee", key: "tradingFee", width: 140 },
    { title: "AvtaMata шимтгэл", dataIndex: "avtamataFee", key: "avtamataFee", width: 140 },
    { title: "Шилжүүлэх дүн", dataIndex: "transferAmount", key: "transferAmount", width: 140 },
    { title: "Данс", dataIndex: "account", key: "account", width: 140 },
  ];

  const activeCustomer = quarterResult?.customer || result?.customer;
  const showDownload = Boolean(quarterResult || result);
  const hasSelectedQuarterUploads = QUARTER_FILE_TYPES.some(({ key }) =>
    isQuarterFileUploaded(selectedUploadYear, selectedUploadQuarter, key)
  );

  return (
    <>
      <div className="page-header">
        <h3>Хуулга</h3>
        <Space>
          {showDownload && (
            <Dropdown
              menu={{
                items: [
                  {
                    key: "excel",
                    icon: <FileExcelOutlined />,
                    label: "Excel (.xlsx)",
                    onClick: downloadExcel,
                  },
                  {
                    key: "pdf",
                    icon: <FilePdfOutlined />,
                    label: "PDF (.pdf)",
                    onClick: () => {
                      downloadPDF().catch((e: unknown) => {
                        console.error("PDF download error:", e);
                        message.error("PDF татахад алдаа гарлаа");
                      });
                    },
                  },
                ],
              }}
            >
              <Button type="primary" icon={<DownloadOutlined />}>Татах</Button>
            </Dropdown>
          )}
          <Button
            icon={showFileUpload ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => setShowFileUpload(!showFileUpload)}
          >
            {showFileUpload ? "Файл нуух" : "Файл удирдлага"}
          </Button>
        </Space>
      </div>

      <div className="page-body">
        {/* File Management Section */}
        {showFileUpload && (
          <Card size="small" title="Файл удирдлага" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 12]} align="middle">
              <Col>
                <Space>
                  <Typography.Text strong>Он сонгох:</Typography.Text>
                  <Select
                    value={selectedUploadYear}
                    onChange={setSelectedUploadYear}
                    style={{ width: 100 }}
                  >
                    {uploadYearOptions.map((y) => (
                      <Select.Option key={y} value={y}>{y}</Select.Option>
                    ))}
                  </Select>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Typography.Text strong>Улирал:</Typography.Text>
                  <Select
                    value={selectedUploadQuarter}
                    onChange={setSelectedUploadQuarter}
                    style={{ width: 100 }}
                  >
                    {HUULGA_QUARTERS.map((quarter) => (
                      <Select.Option key={quarter} value={quarter}>{quarter}</Select.Option>
                    ))}
                  </Select>
                </Space>
              </Col>
              <Col>
                <Space>
                  <InputNumber
                    placeholder="Шинэ он"
                    min={2020}
                    max={2100}
                    value={newYear}
                    onChange={(v) => setNewYear(v)}
                    style={{ width: 100 }}
                  />
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => {
                      if (newYear) {
                        addYearQuarter(newYear, newQuarter);
                        setNewYear(null);
                        message.success(`${newYear} / ${newQuarter} нэмэгдлээ`);
                      }
                    }}
                    disabled={
                      !newYear ||
                      availableYearQuarterKeys.includes(`${newYear}-${newQuarter}`)
                    }
                  >
                    Жил/улирал нэмэх
                  </Button>
                  <Select
                    value={newQuarter}
                    onChange={setNewQuarter}
                    style={{ width: 90 }}
                  >
                    {HUULGA_QUARTERS.map((quarter) => (
                      <Select.Option key={quarter} value={quarter}>{quarter}</Select.Option>
                    ))}
                  </Select>
                </Space>
              </Col>
            </Row>

            <Row style={{ marginTop: 12 }}>
              <Col>
                <Space>
                  {QUARTER_FILE_TYPES.map(({ key, label }) => {
                    const uploaded = isQuarterFileUploaded(
                      selectedUploadYear,
                      selectedUploadQuarter,
                      key
                    );
                    return (
                      <Upload
                        key={key}
                        accept=".csv,.xlsx,.xls"
                        showUploadList={false}
                        beforeUpload={async (file: File) => {
                          const uploadMessageKey = `huulga-quarter-upload-${key}`;
                          try {
                            message.open({
                              key: uploadMessageKey,
                              type: "loading",
                              content: `${label}: Uploading`,
                              duration: 0,
                            });
                            const uploadResult = await handleQuarterFileUpload(
                              key,
                              file,
                              selectedUploadYear,
                              selectedUploadQuarter,
                              (statusText) => {
                                message.open({
                                  key: uploadMessageKey,
                                  type: "loading",
                                  content: `${label}: ${statusText}`,
                                  duration: 0,
                                });
                              }
                            );

                            if (uploadResult.ok) {
                              message.open({
                                key: uploadMessageKey,
                                type: "success",
                                content: `${label}: Saved`,
                                duration: 3,
                              });
                            } else if (uploadResult.status === 413) {
                              message.open({
                                key: uploadMessageKey,
                                type: "error",
                                content: `${label}: Too large`,
                                duration: 5,
                              });
                            } else {
                              message.open({
                                key: uploadMessageKey,
                                type: "error",
                                content: `${label}: Failed`,
                                duration: 5,
                              });
                            }
                          } catch (e) {
                            console.error("Quarter upload unexpected error:", e);
                            message.open({
                              key: uploadMessageKey,
                              type: "error",
                              content: `${label}: Failed`,
                              duration: 5,
                            });
                          }
                          return false;
                        }}
                      >
                        <Button
                          icon={uploaded ? <CheckCircleOutlined /> : <UploadOutlined />}
                          style={
                            uploaded
                              ? { borderColor: "#52c41a", color: "#52c41a" }
                              : undefined
                          }
                        >
                          {label}
                        </Button>
                      </Upload>
                    );
                  })}
                  <Popconfirm
                    title={`${selectedUploadYear} / ${selectedUploadQuarter} дээрх 4 файлыг backend-ээс устгах уу?`}
                    okText="Устгах"
                    cancelText="Болих"
                    onConfirm={async () => {
                      try {
                        await deleteYearQuarterFiles(
                          selectedUploadYear,
                          selectedUploadQuarter
                        );
                        message.success(
                          `${selectedUploadYear} / ${selectedUploadQuarter} файлууд устгагдлаа`
                        );
                      } catch (e) {
                        console.error("Delete quarter files error:", e);
                        message.error("Файлуудыг устгахад алдаа гарлаа");
                      }
                    }}
                    disabled={!hasSelectedQuarterUploads}
                  >
                    <Button danger disabled={!hasSelectedQuarterUploads}>
                      4 файлыг устгах
                    </Button>
                  </Popconfirm>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        {/* Search + Filter */}
        <Row gutter={[16, 12]} align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Search
              placeholder="Имейл хаягаар хайх"
              enterButton="Хайх"
              size="large"
              loading={loading || dataLoading}
              disabled={dataLoading}
              onSearch={searchQuarter}
              style={{ width: 400 }}
            />
          </Col>
          <Col>
            <Radio.Group
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="large"
            >
              <Radio.Button value="total">Нийт</Radio.Button>
              <Radio.Button value="yearly">Жил, улирал сонгох</Radio.Button>
            </Radio.Group>
          </Col>
          {filterMode === "yearly" && availableYearQuarterKeys.length > 0 && (
            <Col>
              <Checkbox.Group
                value={selectedQuarterKeys}
                onChange={(vals) => setSelectedQuarterKeys(vals as string[])}
              >
                <Space>
                  {availableYearQuarterKeys.map((key) => (
                    <Checkbox key={key} value={key}>
                      {key.replace("-", " / ")}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </Col>
          )}
        </Row>

        {error && (
          <Card style={{ marginBottom: 16 }}>
            <p style={{ color: "#ff4d4f", margin: 0 }}>{error}</p>
          </Card>
        )}

        {quarterResult && (
          <>
            <Card
              size="small"
              style={{
                marginBottom: 16,
                background: "linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)",
                borderRadius: 12,
                border: "none",
              }}
            >
              <Row align="middle" gutter={[24, 12]}>
                <Col>
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%", background: "#1890ff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <UserOutlined style={{ fontSize: 24, color: "#fff" }} />
                  </div>
                </Col>
                <Col flex="auto">
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    {quarterResult.customer.lastName} {quarterResult.customer.firstName}
                  </Typography.Title>
                  <Typography.Text type="secondary">{quarterResult.customer.register}</Typography.Text>
                </Col>
              </Row>
              <Row gutter={[32, 8]} style={{ marginTop: 16 }}>
                <Col>
                  <Space size={6}>
                    <MailOutlined style={{ color: "#1890ff" }} />
                    <Typography.Text>{quarterResult.customer.email}</Typography.Text>
                  </Space>
                </Col>
                <Col>
                  <Space size={6}>
                    <PhoneOutlined style={{ color: "#1890ff" }} />
                    <Typography.Text>{quarterResult.customer.phone}</Typography.Text>
                  </Space>
                </Col>
                <Col>
                  <Space size={6}>
                    <IdcardOutlined style={{ color: "#1890ff" }} />
                    <Typography.Text>{quarterResult.customer.register}</Typography.Text>
                  </Space>
                </Col>
              </Row>
            </Card>

            <Tabs defaultActiveKey="ha" type="card">
              <TabPane
                tab={`Худалдан авалт (${quarterResult.hudaldanAvalt.length})`}
                key="ha"
              >
                <Table
                  dataSource={quarterResult.hudaldanAvalt}
                  columns={quarterPurchaseColumns}
                  size="small"
                  bordered
                  pagination={false}
                  scroll={{ x: 1400 }}
                />
              </TabPane>

              <TabPane
                tab={`Цэнэглэлт (${quarterResult.tseneglelt.length})`}
                key="ts"
              >
                <Table
                  dataSource={quarterResult.tseneglelt}
                  columns={quarterDepositColumns}
                  size="small"
                  bordered
                  pagination={false}
                  scroll={{ x: 1100 }}
                />
              </TabPane>

              <TabPane
                tab={`Буцаалт (${quarterResult.bondRefunds.length + quarterResult.stockRefunds.length})`}
                key="bu"
              >
                <Typography.Title level={5} style={{ marginTop: 0 }}>
                  Буцаалт (Бонд)
                </Typography.Title>
                <Table
                  dataSource={quarterResult.bondRefunds}
                  columns={quarterBondRefundColumns}
                  size="small"
                  bordered
                  pagination={false}
                  scroll={{ x: 1800 }}
                />

                <Divider />

                <Typography.Title level={5}>
                  Буцаалт (Хувьцаа)
                </Typography.Title>
                <Table
                  dataSource={quarterResult.stockRefunds}
                  columns={quarterStockRefundColumns}
                  size="small"
                  bordered
                  pagination={false}
                  scroll={{ x: 1500 }}
                />
              </TabPane>
            </Tabs>
          </>
        )}

        {!quarterResult && result && (
          <>
            {/* Customer Info Card */}
            <Card
              size="small"
              style={{
                marginBottom: 16,
                background: "linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)",
                borderRadius: 12,
                border: "none",
              }}
            >
              <Row align="middle" gutter={[24, 12]}>
                <Col>
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%", background: "#1890ff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <UserOutlined style={{ fontSize: 24, color: "#fff" }} />
                  </div>
                </Col>
                <Col flex="auto">
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    {activeCustomer?.lastName} {activeCustomer?.firstName}
                  </Typography.Title>
                  <Typography.Text type="secondary">{activeCustomer?.register}</Typography.Text>
                </Col>
              </Row>
              <Row gutter={[32, 8]} style={{ marginTop: 16 }}>
                <Col>
                  <Space size={6}>
                    <MailOutlined style={{ color: "#1890ff" }} />
                    <Typography.Text>{activeCustomer?.email}</Typography.Text>
                  </Space>
                </Col>
                <Col>
                  <Space size={6}>
                    <PhoneOutlined style={{ color: "#1890ff" }} />
                    <Typography.Text>{activeCustomer?.phone}</Typography.Text>
                  </Space>
                </Col>
                <Col>
                  <Space size={6}>
                    <IdcardOutlined style={{ color: "#1890ff" }} />
                    <Typography.Text>{activeCustomer?.register}</Typography.Text>
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* Results in 3 Tabs */}
            <Tabs defaultActiveKey="oz" type="card">
              <TabPane
                tab={`Орлого зарлага (${result.orlogoZarlaga.length})`}
                key="oz"
              >
                <Table
                  dataSource={result.orlogoZarlaga}
                  columns={orlogoZarlagaColumns}
                  size="small"
                  bordered
                  pagination={false}
                  scroll={{ x: 1000 }}
                />
              </TabPane>

              <TabPane
                tab={`Худалдан авалт (${result.hudaldanAvalt.length})`}
                key="ha"
              >
                <Table
                  dataSource={result.hudaldanAvalt}
                  columns={hudaldanAvaltColumns}
                  size="small"
                  bordered
                  pagination={false}
                  scroll={{ x: 1200 }}
                />
              </TabPane>

              <TabPane
                tab={`Буцаалт (${result.buitsaalt.length})`}
                key="bu"
              >
                <Table
                  dataSource={result.buitsaalt}
                  columns={buitsaaltColumns}
                  size="small"
                  bordered
                  pagination={false}
                  scroll={{ x: 1500 }}
                />
              </TabPane>
            </Tabs>
          </>
        )}
      </div>
    </>
  );
};
